import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Mic, MicOff, PhoneOff, Send, Keyboard, MicIcon } from 'lucide-react';
import { brandColors } from '@/design';
import type { TranscriptEntry } from '../../hooks/useVoiceSession';
import type { VoiceStatus } from '../../hooks/useVoiceSession';

interface VoiceInterfaceProps {
  status: VoiceStatus;
  transcript: TranscriptEntry[];
  isMuted: boolean;
  isAssistantSpeaking: boolean;
  onToggleMute: () => void;
  onEnd: () => void;
  onSendText?: (text: string) => void;
  onSwitchToText?: () => void;
}

export default function VoiceInterface({
  status,
  transcript,
  isMuted,
  isAssistantSpeaking,
  onToggleMute,
  onEnd,
  onSendText,
  onSwitchToText,
}: VoiceInterfaceProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript]);

  useEffect(() => {
    if (showTextInput && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showTextInput]);

  function handleSendText() {
    const text = textInput.trim();
    if (!text || !onSendText) return;
    onSendText(text);
    setTextInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  }

  function handleTextareaInput() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }

  const statusLabel =
    status === 'connecting' ? 'Connecting…'
    : status === 'active' && isAssistantSpeaking ? 'Flank is speaking'
    : status === 'active' ? (showTextInput ? 'Type or speak' : 'Listening…')
    : status === 'ending' ? 'Wrapping up…'
    : '';

  return (
    <div className="flex flex-col h-full">
      {/* Status label */}
      <div className="flex items-center justify-center gap-2 pt-4 pb-2 shrink-0">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{
            background: status === 'active'
              ? (isAssistantSpeaking ? brandColors.teal : 'var(--color-success)')
              : 'var(--color-text-muted)',
          }}
        />
        <span className="text-xs text-[var(--color-text-muted)]">{statusLabel}</span>
      </div>

      {/* Transcript */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-2"
      >
        <div className="w-full max-w-4xl mx-auto flex flex-col gap-3">
          {transcript.length === 0 && status === 'active' && (
            <div className="flex-1 flex items-center justify-center py-8">
              <p className="text-sm text-[var(--color-text-muted)] text-center max-w-xs">
                {showTextInput ? 'Type a message or start speaking' : 'Start speaking — Flank is listening'}
              </p>
            </div>
          )}
          {transcript.map((entry, i) => (
            <TranscriptBubble key={i} entry={entry} />
          ))}
        </div>
      </div>

      {showTextInput && status === 'active' ? (
        /* Text input mode */
        <div className="shrink-0 border-t border-[var(--color-border)] px-4 py-3" style={{ background: 'var(--color-background)' }}>
          <div className="w-full max-w-4xl mx-auto flex flex-col gap-3">
            <div className="flex items-end gap-3">
              <textarea
                ref={textareaRef}
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleTextareaInput}
                placeholder="Type a message…"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-border-focus)] transition-colors"
                style={{ minHeight: '42px', maxHeight: '120px' }}
              />
              <button
                onClick={handleSendText}
                disabled={!textInput.trim()}
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
                style={{ background: 'var(--color-brand-primary)' }}
              >
                <Send size={16} color="white" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  if (isMuted) onToggleMute();
                  setShowTextInput(false);
                }}
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
              >
                <MicIcon size={14} />
                Switch to voice
              </button>
              <button
                onClick={onEnd}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white transition-all hover:opacity-90 active:scale-95"
                style={{ background: 'var(--color-error)' }}
              >
                <PhoneOff size={12} />
                End
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Voice orb + controls */
        <div className="shrink-0 flex flex-col items-center gap-6 py-8">
          <div className="relative flex items-center justify-center">
            {isAssistantSpeaking && (
              <>
                <span
                  className="absolute w-40 h-40 rounded-full animate-ping pointer-events-none"
                  style={{ background: brandColors.teal, opacity: 0.12 }}
                />
                <span
                  className="absolute w-32 h-32 rounded-full animate-ping pointer-events-none"
                  style={{
                    background: brandColors.teal,
                    opacity: 0.18,
                    animationDelay: '0.2s',
                    animationDuration: '1.2s',
                  }}
                />
              </>
            )}
            {status === 'active' && !isAssistantSpeaking && !isMuted && (
              <span
                className="absolute w-28 h-28 rounded-full pointer-events-none"
                style={{
                  background: `${brandColors.teal}18`,
                  border: `1px solid ${brandColors.teal}40`,
                }}
              />
            )}
            <button
              onClick={onToggleMute}
              disabled={status !== 'active'}
              className="relative w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 disabled:opacity-50"
              style={{
                background: isMuted
                  ? 'var(--color-surface)'
                  : `linear-gradient(135deg, ${brandColors.teal}, ${brandColors.lightBlue})`,
              }}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted
                ? <MicOff size={26} style={{ color: 'var(--color-text-muted)' }} />
                : <Mic size={26} color="white" />
              }
            </button>
          </div>

          <div className="flex flex-col items-center gap-3">
            {status === 'active' && (
              <p className="text-xs text-[var(--color-text-muted)]">
                {isMuted ? 'Tap orb to unmute' : 'Tap orb to mute'}
              </p>
            )}
            {status === 'active' && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (onSwitchToText) {
                      onSwitchToText();
                    } else {
                      if (!isMuted) onToggleMute();
                      setShowTextInput(true);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:bg-[var(--color-surface)]"
                  style={{ color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                >
                  <Keyboard size={14} />
                  Type instead
                </button>
                <button
                  onClick={onEnd}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'var(--color-error)' }}
                >
                  <PhoneOff size={14} />
                  End session
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transcript bubble ─────────────────────────────────────────────────────

function TranscriptBubble({ entry }: { entry: TranscriptEntry }) {
  const isUser = entry.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
        style={
          isUser
            ? {
                background: `${brandColors.teal}22`,
                color: 'var(--color-text-primary)',
                borderBottomRightRadius: 4,
              }
            : {
                background: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                borderBottomLeftRadius: 4,
              }
        }
      >
        <span>{entry.text}</span>
        {entry.isStreaming && (
          <span
            className="inline-block ml-1 align-middle animate-pulse"
            style={{ color: 'var(--color-text-muted)' }}
            aria-hidden="true"
          >
            •
          </span>
        )}
      </div>
    </div>
  );
}
